// operatorConfig.ts — Operator Control Center™ Runtime Configuration
// Sprint 080: THE SINGLE RUNTIME SOURCE OF TRUTH for all operator configuration.
//
// ARCHITECTURE RULE:
//   All engines read configuration from this file via loadOperatorConfig().
//   No engine may own its own hardcoded preferences.
//   No hardcoded values outside this system.
//
// STORAGE: localStorage — key nomad:occ:config:v1
// FORMAT:  JSON, deep-merged with DEFAULT_OCC_CONFIG on load (forward-compatible)

import { storageManager } from '@/services/storage/StorageManager';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DayOfWeek =
  | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday'
  | 'Friday' | 'Saturday' | 'Sunday';

export type MissionMode =
  | 'income_acceleration' | 'career' | 'business' | 'learning' | 'passive';

export type OpportunityWeight = 'off' | 'low' | 'medium' | 'high' | 'highest';

export type PaymentMethod =
  | 'paypal' | 'bank' | 'wise' | 'payoneer' | 'crypto' | 'gift_cards';

export type ApplicationRequirementPolicy = 'allow' | 'avoid' | 'never';

export type LearningSensitivity = 'low' | 'medium' | 'high';

// ─── Units types ─────────────────────────────────────────────────────────────

export type TemperatureUnit    = 'fahrenheit' | 'celsius';
export type DistanceUnit       = 'miles' | 'kilometers';
export type SpeedUnit          = 'mph' | 'kmh';
export type CurrencyDisplayMode = 'automatic' | 'manual';

export interface OperatorUnitsConfig {
  temperature:     TemperatureUnit;
  distance:        DistanceUnit;
  speed:           SpeedUnit;
  currencyDisplay: CurrencyDisplayMode;
}

export const ALL_DAYS: DayOfWeek[] = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

export const ALL_OPPORTUNITY_TYPES = [
  // ── Sprint 091: Absolute Operator Authority™ (Build 1.84.0) ─────────────
  // All types the OCC can weight/disable. New types added in this sprint
  // are marked with †. Existing types unchanged (scoring algorithms unmodified).
  'AI Training',
  'AI Evaluation',          // † Sprint 091 — AI Evaluation tasks (distinct from prompt/search eval)
  'User Testing',
  'Research',
  'Data Annotation',
  'Microtasks',
  'Freelance',
  'Contract',
  'Traditional Employment',
  'Surveys',
  'Focus Groups',
  'Website Testing',
  'App Testing',
  'Prompt Evaluation',
  'Search Evaluation',
  'Content Moderation',
  'Transcription',
  'Translation',
  'Data Entry',
  'Writing',                // † Sprint 091 — Content writing, copywriting, ghostwriting
  'Coding',                 // † Sprint 091 — Programming, software development tasks
  'Design',                 // † Sprint 091 — Graphic, UI/UX, logo design tasks
  'Customer Support',       // † Sprint 091 — Support, help desk, chat operator roles
  'Sales',                  // † Sprint 091 — Sales, outreach, SDR, BDR roles
  'Administration',         // † Sprint 091 — Admin, virtual assistant, data admin roles
] as const;
export type OpportunityType = typeof ALL_OPPORTUNITY_TYPES[number];

export const ALL_APPLICATION_REQUIREMENTS = [
  'Resume',
  'Cover Letter',
  'Assessment',
  'Interview',
  'Video Interview',
  'Phone Interview',
  'Background Check',
  'Portfolio',
] as const;
export type ApplicationRequirement = typeof ALL_APPLICATION_REQUIREMENTS[number];

// ─── DaySchedule ──────────────────────────────────────────────────────────────

export interface DaySchedule {
  enabled:   boolean;
  hours:     number;
  unlimited: boolean;
}

// ─── OperatorCCConfig ─────────────────────────────────────────────────────────

export interface OperatorCCConfig {
  // ── MISSION ────────────────────────────────────────────────────────────────
  dailyGoal:   number;
  weeklyGoal:  number;
  monthlyGoal: number;
  yearlyGoal:  number;
  missionMode: MissionMode;

  // ── SCHEDULE ───────────────────────────────────────────────────────────────
  schedule: Record<DayOfWeek, DaySchedule>;

  // ── REGIONS ────────────────────────────────────────────────────────────────
  country:               string;
  legalWorkEligibility:  'worldwide' | 'us_eligible';
  excludeCountries:      string[];

  // ── LANGUAGES ──────────────────────────────────────────────────────────────
  languages:          string[];
  translationEnabled: boolean;
  acceptNonEnglish:   boolean;

  // ── OPPORTUNITY TYPES ──────────────────────────────────────────────────────
  opportunityTypeWeights: Record<OpportunityType, OpportunityWeight>;

  // ── PAYOUT ─────────────────────────────────────────────────────────────────
  minHourlyRate:           number;
  minPayout:               number;
  preferredPaymentMethods: PaymentMethod[];

  // ── APPLICATIONS ───────────────────────────────────────────────────────────
  applicationRequirements: Record<ApplicationRequirement, ApplicationRequirementPolicy>;

  // ── DEVICES ────────────────────────────────────────────────────────────────
  devices: {
    mobile:     boolean;
    desktop:    boolean;
    tablet:     boolean;
    windows:    boolean;
    mac:        boolean;
    linux:      boolean;
    microphone: boolean;
    webcam:     boolean;
    vpn:        boolean;
  };

  // ── LEARNING ───────────────────────────────────────────────────────────────
  trustHistoryOverInternet: boolean;
  learningSensitivity:      LearningSensitivity;

  // ── NOTIFICATIONS ──────────────────────────────────────────────────────────
  notifications: {
    morningBriefing:           boolean;
    highConfidenceOpportunity: boolean;
    paymentReminder:           boolean;
    applicationFollowUp:       boolean;
    weeklyReport:              boolean;
  };

  // ── UNITS ──────────────────────────────────────────────────────────────────
  units: OperatorUnitsConfig;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_OCC_CONFIG: OperatorCCConfig = {
  dailyGoal:   25,
  weeklyGoal:  175,
  monthlyGoal: 750,
  yearlyGoal:  9100,
  missionMode: 'income_acceleration',

  schedule: {
    Monday:    { enabled: true, hours: 5, unlimited: false },
    Tuesday:   { enabled: true, hours: 5, unlimited: false },
    Wednesday: { enabled: true, hours: 5, unlimited: false },
    Thursday:  { enabled: true, hours: 5, unlimited: false },
    Friday:    { enabled: true, hours: 8, unlimited: true  },
    Saturday:  { enabled: true, hours: 8, unlimited: true  },
    Sunday:    { enabled: true, hours: 8, unlimited: true  },
  },

  country:              'US',
  legalWorkEligibility: 'us_eligible',
  excludeCountries:     [],

  languages:          ['English'],
  translationEnabled: true,
  acceptNonEnglish:   true,

  opportunityTypeWeights: {
    'AI Training':            'highest',
    'AI Evaluation':          'highest',    // † Sprint 091
    'User Testing':           'highest',
    'Research':               'high',
    'Data Annotation':        'high',
    'Microtasks':             'high',
    'Freelance':              'medium',
    'Contract':               'medium',
    'Traditional Employment': 'low',
    'Surveys':                'medium',
    'Focus Groups':           'high',
    'Website Testing':        'high',
    'App Testing':            'high',
    'Prompt Evaluation':      'highest',
    'Search Evaluation':      'highest',
    'Content Moderation':     'low',
    'Transcription':          'medium',
    'Translation':            'medium',
    'Data Entry':             'low',
    'Writing':                'medium',     // † Sprint 091
    'Coding':                 'medium',     // † Sprint 091
    'Design':                 'medium',     // † Sprint 091
    'Customer Support':       'low',        // † Sprint 091
    'Sales':                  'low',        // † Sprint 091
    'Administration':         'low',        // † Sprint 091
  },

  minHourlyRate:           0,
  minPayout:               0,
  preferredPaymentMethods: ['paypal', 'bank', 'wise'],

  applicationRequirements: {
    'Resume':           'allow',
    'Cover Letter':     'allow',
    'Assessment':       'allow',
    'Interview':        'avoid',
    'Video Interview':  'avoid',
    'Phone Interview':  'avoid',
    'Background Check': 'never',
    'Portfolio':        'allow',
  },

  devices: {
    mobile:     true,
    desktop:    true,
    tablet:     true,
    windows:    true,
    mac:        true,
    linux:      true,
    microphone: false,
    webcam:     false,
    vpn:        false,
  },

  trustHistoryOverInternet: true,
  learningSensitivity:      'medium',

  notifications: {
    morningBriefing:           true,
    highConfidenceOpportunity: true,
    paymentReminder:           true,
    applicationFollowUp:       true,
    weeklyReport:              true,
  },

  units: {
    temperature:     'fahrenheit',
    distance:        'miles',
    speed:           'mph',
    currencyDisplay: 'automatic',
  },
};

// ─── Persistence ──────────────────────────────────────────────────────────────

const STORAGE_KEY  = 'nomad:occ:config:v1';
const PRESETS_KEY  = 'nomad:occ:presets:v1';

/** Load the operator config from localStorage, deep-merged with defaults. */
export function loadOperatorConfig(): OperatorCCConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_OCC_CONFIG;
    return deepMerge(DEFAULT_OCC_CONFIG, JSON.parse(raw) as Partial<OperatorCCConfig>);
  } catch {
    return DEFAULT_OCC_CONFIG;
  }
}

/** Persist the operator config to localStorage. */
export function saveOperatorConfig(config: OperatorCCConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  // SUPABASE FOUNDATION™: write-through to cloud (queues offline, never throws).
  storageManager.push('operator', 'config', config);
}

/**
 * SUPABASE FOUNDATION™ boot hydration: adopt the cloud config only if strictly
 * newer than the last local write (StorageManager enforces this). Broadcasts
 * via the existing nomad:occ-saved live-update channel so consumers reload.
 */
export async function hydrateOperatorConfigFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<Partial<OperatorCCConfig>>('operator', 'config');
  if (!cloud || typeof cloud !== 'object') return false;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud)); } catch { return false; }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('nomad:occ-saved', { detail: { source: 'cloud' } }));
  }
  return true;
}

// ─── Presets ──────────────────────────────────────────────────────────────────

export interface OperatorPreset {
  id:          string;
  name:        string;
  description: string;
  createdAt:   string;
  isBuiltIn:   boolean;
  config:      Partial<OperatorCCConfig>;
}

export const BUILT_IN_PRESETS: OperatorPreset[] = [
  {
    id: 'builtin:weekend-cash-sprint', name: 'Weekend Cash Sprint',
    description: 'Maximize Fri–Sun. Disable weekdays. Full unlimited schedule.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      missionMode: 'income_acceleration',
      schedule: {
        Monday:    { enabled: false, hours: 0, unlimited: false },
        Tuesday:   { enabled: false, hours: 0, unlimited: false },
        Wednesday: { enabled: false, hours: 0, unlimited: false },
        Thursday:  { enabled: false, hours: 0, unlimited: false },
        Friday:    { enabled: true,  hours: 8, unlimited: true  },
        Saturday:  { enabled: true,  hours: 8, unlimited: true  },
        Sunday:    { enabled: true,  hours: 8, unlimited: true  },
      },
    },
  },
  {
    id: 'builtin:ai-money-mode', name: 'AI Money Mode',
    description: 'All-in on AI Training, Evaluation, and Annotation. Everything else deprioritized.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      missionMode: 'income_acceleration',
      opportunityTypeWeights: {
        ...DEFAULT_OCC_CONFIG.opportunityTypeWeights,
        'AI Training': 'highest', 'Prompt Evaluation': 'highest',
        'Search Evaluation': 'highest', 'Data Annotation': 'highest',
        'User Testing': 'high', 'Research': 'medium',
        'Surveys': 'low', 'Traditional Employment': 'off', 'Data Entry': 'off',
        'Content Moderation': 'off',
      },
    },
  },
  {
    id: 'builtin:travel-mode', name: 'Travel Mode',
    description: 'Mobile-first. Short tasks. No interviews, no background checks.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      applicationRequirements: {
        ...DEFAULT_OCC_CONFIG.applicationRequirements,
        'Interview': 'never', 'Video Interview': 'never',
        'Phone Interview': 'never', 'Background Check': 'never',
      },
    },
  },
  {
    id: 'builtin:research-hunter', name: 'Research Hunter',
    description: 'Prioritize research studies, focus groups, and UX sessions.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      opportunityTypeWeights: {
        ...DEFAULT_OCC_CONFIG.opportunityTypeWeights,
        'Research': 'highest', 'Focus Groups': 'highest',
        'User Testing': 'highest', 'Website Testing': 'highest', 'App Testing': 'highest',
      },
    },
  },
  {
    id: 'builtin:career-mode', name: 'Career Mode',
    description: 'Open to traditional employment. Accept interviews and assessments.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      missionMode: 'career',
      opportunityTypeWeights: {
        ...DEFAULT_OCC_CONFIG.opportunityTypeWeights,
        'Traditional Employment': 'highest', 'Contract': 'high', 'Freelance': 'high',
      },
      applicationRequirements: {
        ...DEFAULT_OCC_CONFIG.applicationRequirements,
        'Interview': 'allow', 'Video Interview': 'allow',
        'Phone Interview': 'allow', 'Background Check': 'allow',
      },
    },
  },
  {
    id: 'builtin:passive-mode', name: 'Passive Mode',
    description: 'Low effort, high autopilot. Microtasks and surveys only.',
    createdAt: '2026-07-16', isBuiltIn: true,
    config: {
      missionMode: 'passive',
      opportunityTypeWeights: {
        ...DEFAULT_OCC_CONFIG.opportunityTypeWeights,
        'AI Training': 'high', 'Microtasks': 'highest', 'Surveys': 'highest',
        'Traditional Employment': 'off', 'Contract': 'off', 'Freelance': 'low',
      },
    },
  },
];

/** Load presets: built-ins + any user-saved presets from localStorage. */
export function loadPresets(): OperatorPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [...BUILT_IN_PRESETS];
    const saved: OperatorPreset[] = JSON.parse(raw);
    return [
      ...BUILT_IN_PRESETS,
      ...saved.filter(p => !p.id.startsWith('builtin:')),
    ];
  } catch {
    return [...BUILT_IN_PRESETS];
  }
}

/** Persist user-created presets. Built-ins are never stored in localStorage. */
export function savePresets(presets: OperatorPreset[]): void {
  const userPresets = presets.filter(p => !p.id.startsWith('builtin:'));
  localStorage.setItem(PRESETS_KEY, JSON.stringify(userPresets));
}

// ─── Utilities ────────────────────────────────────────────────────────────────

/** Deep merge: defaults as base, override applied on top. Arrays are replaced not merged. */
function deepMerge<T extends object>(defaults: T, override: Partial<T>): T {
  const result = { ...defaults } as T;
  for (const key of Object.keys(override) as (keyof T)[]) {
    const val = override[key];
    if (val === undefined || val === null) continue;
    if (
      typeof val === 'object' &&
      !Array.isArray(val) &&
      typeof defaults[key] === 'object' &&
      !Array.isArray(defaults[key])
    ) {
      result[key] = deepMerge(defaults[key] as object, val as object) as T[keyof T];
    } else {
      result[key] = val as T[keyof T];
    }
  }
  return result;
}

/** Compute total available hours per week from schedule config. */
export function computeWeeklyHours(schedule: Record<DayOfWeek, DaySchedule>): number {
  return ALL_DAYS.reduce((sum, day) => {
    const d = schedule[day];
    if (!d.enabled) return sum;
    return sum + (d.unlimited ? 8 : d.hours);
  }, 0);
}

/** Compute projected hourly rate from weekly goal and schedule. */
export function computeProjectedHourlyRate(config: OperatorCCConfig): number {
  const hours = computeWeeklyHours(config.schedule);
  if (hours === 0) return 0;
  return Math.round((config.weeklyGoal / hours) * 100) / 100;
}

/** Estimate opportunity reach from opportunity type weights. */
export function computeOpportunityReach(weights: Record<OpportunityType, OpportunityWeight>): number {
  const weightValues: Record<OpportunityWeight, number> = {
    off: 0, low: 5, medium: 10, high: 15, highest: 20,
  };
  return ALL_OPPORTUNITY_TYPES.reduce((sum, t) => sum + weightValues[weights[t]], 0);
}

/** Apply a preset to a base config (deep merge preset.config on top of base). */
export function applyPreset(base: OperatorCCConfig, preset: OperatorPreset): OperatorCCConfig {
  return deepMerge(base, preset.config as Partial<OperatorCCConfig>);
}
